import type { ApiClient, ApiClientError } from '@acies/platform';
import { Effect, Schema } from 'effect';
import { HttpBody, HttpClientRequest, HttpClientResponse } from 'effect/http';
import { Configuration, type ISentConfigurationParameter, ModelSelf, SentConfigurationParameters } from '../domain/configurations';
import type { ModelPosition } from '../domain/positions';
import { PositiveInteger } from '../domain/shared';
import { type ApiServices, runCall } from './runtime';
import { callDependencies, type ModelPath } from './shared';

/**
 * Where configurations live, in full.
 *
 * Not an {@link EnginePath}: this resource sits at the API's own root rather than inside the account's store, and which store a configuration is for is said in the body instead. So there is no engine to read off the account here, and the path is a constant.
 */
const resource = '/configurator/configurations';

/**
 * Which kind of configuration is being asked for.
 *
 * `one` is the AciesOne kind, and the only kind the Configurator makes. A literal rather than a string, because it is not a choice the caller has: a body naming another kind would be a body this module had no business sending.
 */
const ConfigurationTypeSelf = Schema.Literal('/configurator/configuration-types/one');

/**
 * The body a configuration is asked for with.
 *
 * A `Schema.Struct` rather than a `Schema.Class`, like `CreateCalculationBody`: it is built for one request and encoded straight onto it, so it is never held, never extended and never constructed by hand.
 *
 * One body for both calls, because there is one call: a configuration is never changed, it is made again from the answers it is to have — see {@link ConfigurationCalls.createConfigurationFrom}. What tells the two apart is what goes in `parameters.items`, and nothing else.
 *
 * `model` is typed by the very schema that reads a model back off a configuration — see {@link ModelSelf} — which is what makes the two agree by construction rather than by care. The engine writes model links one way in a search result and accepts them another way here, so the shared schema is doing real work: the
 * prefixed form does not compile. It is also what lets a configuration be made from another one without asking the account where its model lives: the answer names it in the shape the request wants.
 *
 * `items` is typed by the union a configuration's parameters are read with, widened by the one kind that is only ever sent — a geometry picked for the engine to measure, see `PickedGeometryParameter` — so what goes back is what came in, changed where the user changed it. Encoding a parameter class writes its schema
 * fields alone — the `label` and `key` a row reads off one are getters, and stay behind. Empty is what a first configuration sends: it means "whatever the model says", so what comes back is the model's own defaults, which is where configuring one starts.
 */
const ConfigurationBody = Schema.Struct({
	height: PositiveInteger,
	model: Schema.Struct({ self: ModelSelf }),
	parameters: Schema.Struct({ items: Schema.Array(SentConfigurationParameters) }),
	quantity: PositiveInteger,
	type: Schema.Struct({ self: ConfigurationTypeSelf }),
	width: PositiveInteger,
});

const encodeConfigurationBody = HttpClientRequest.schemaBodyJson(ConfigurationBody);

/**
 * The configuration itself, unwrapped.
 *
 * No envelope, unlike a calculation's `result` and unlike a collection's `items`: this resource answers with the configuration at the top level, so there is nothing to unwrap.
 */
const decodeConfiguration = HttpClientResponse.schemaBodyJson(Configuration);

/**
 * Everything making a configuration can fail with.
 *
 * The three a POST that encodes a body and decodes an answer can fail with, and no more. There is no waiting here and so nothing to time out: unlike a calculation, which the engine takes on and works out afterwards, a configuration comes back complete in the one answer.
 *
 * A model the store does not have is not among them either — that is the API refusing the request, which arrives as an {@link ApiClientError} like any other refusal.
 */
export type CreateConfigurationError = ApiClientError | HttpBody.HttpBodyError | Schema.SchemaError;

/**
 * Everything making a configuration from another one can fail with.
 *
 * The same three as making a first one, and for the same reason: it is the same POST, with answers in the body rather than none. A parameter the store refuses — a typed key it has no value for — is the API refusing the request, and arrives as an {@link ApiClientError} like any other refusal.
 */
export type CreateConfigurationFromError = ApiClientError | HttpBody.HttpBodyError | Schema.SchemaError;

/** The calls against the Configurator's configurations. */
export interface ConfigurationCalls {
	/**
	 * Makes a configuration for one position, and answers with the model's defaults.
	 *
	 * The position is read for its model, its measurements and how many of it are wanted. What the engine may configure depends on how big the window is, so the width and the height go with the request, and the quantity goes with them so the calculation that prices the configuration reads it from there.
	 */
	readonly createConfiguration: (position: ModelPosition) => Effect.Effect<Configuration, CreateConfigurationError>;
	/**
	 * Makes a configuration of the same model with the answers it is given, and answers with it as the API made it.
	 *
	 * How a changed answer is taken: nothing is changed in place, and the configuration handed over is not addressed at all. It is read for its model alone, and a new configuration is asked for with that and the answers — the same POST {@link ConfigurationCalls.createConfiguration} makes, with the answers in the body
	 * rather than an empty list. The one it was made from is left where it is, for the API to let go of.
	 *
	 * The answer is what matters, and it is not what was sent: parameters are coupled, so an answer to one can move others, and only the API knows which. A caller that kept what it sent would be keeping a configuration the API disagrees with — and, now, one at a path that names nothing it made.
	 *
	 * The answers are handed over apart from the configuration rather than on it, because one of them may be a kind the configuration cannot hold: a geometry picked for the engine to measure has no measurements yet, and a configuration only holds parameters as the engine answers with them.
	 *
	 * The parameters go over as they came, `self` and all, naming the configuration they were read off. The API is what makes the new paths, and what the old ones mean to it is its own business; nothing here builds a path.
	 *
	 * The measurements and the quantity are read off the position rather than the configuration: the position is what the user measures and counts, so what it holds now is what the engine is to configure for.
	 */
	readonly createConfigurationFrom: (position: ModelPosition, configuration: Configuration, items: ReadonlyArray<ISentConfigurationParameter>) => Effect.Effect<Configuration, CreateConfigurationFromError>;
}

/** Implements the configuration calls over the client and the way a model is named to the API. */
export function makeConfigurationCalls(client: typeof ApiClient.Service, modelPath: ModelPath): ConfigurationCalls {
	return {
		createConfiguration: ({ height, key, quantity, width }) =>
			Effect.gen(function* () {
				const model = yield* modelPath(key);
				const request = yield* encodeConfigurationBody(HttpClientRequest.post(resource), {
					height,
					model: { self: model },
					parameters: { items: [] },
					quantity,
					type: { self: '/configurator/configuration-types/one' },
					width,
				});
				const response = yield* client.execute(request);
				return yield* decodeConfiguration(response);
			}),
		createConfigurationFrom: ({ height, quantity, width }, configuration, items) =>
			Effect.gen(function* () {
				// the model as the answer named it, rather than one built from the account's store: a
				// configuration already says which model it is of, in the very shape this request wants
				const request = yield* encodeConfigurationBody(HttpClientRequest.post(resource), {
					height,
					model: { self: configuration.model.self },
					parameters: { items },
					quantity,
					type: { self: '/configurator/configuration-types/one' },
					width,
				});
				const response = yield* client.execute(request);
				return yield* decodeConfiguration(response);
			}),
	};
}

/**
 * Makes a configuration for one model position, from outside Effect.
 *
 * The awaitable form of {@link ConfigurationCalls.createConfiguration}, for the machine that makes a position. A failure comes back as itself rather than as a bare `false` — see {@link runCall} — because what stopped a position being configured is what has to be said about it.
 */
export const createConfiguration = (position: ModelPosition): Promise<Configuration | CreateConfigurationError> => runCall(() => createConfigurationEffect(position));

/**
 * Makes a configuration for one model position, as an Effect over the Configurator's services.
 *
 * For a caller that forks the call rather than awaiting it, so it can be interrupted when it is overtaken — see `ConfiguratorMachine`. Interrupted, the request is aborted and nothing comes back.
 */
export const createConfigurationEffect = (position: ModelPosition): Effect.Effect<Configuration, CreateConfigurationError, ApiServices> => callDependencies.pipe(Effect.flatMap(({ client, modelPath }) => makeConfigurationCalls(client, modelPath).createConfiguration(position)));

/**
 * Makes a configuration from another one, from outside Effect.
 *
 * The awaitable form of {@link ConfigurationCalls.createConfigurationFrom}, for the machine that answers a changed answer. A failure comes back as itself rather than as a bare `false` — see {@link runCall} — because what stopped an answer being taken is what the row has to say about it.
 */
export const createConfigurationFrom = (position: ModelPosition, configuration: Configuration, items: ReadonlyArray<ISentConfigurationParameter>): Promise<Configuration | CreateConfigurationFromError> => runCall(() => createConfigurationFromEffect(position, configuration, items));

/**
 * Makes a configuration from another one, as an Effect over the Configurator's services.
 *
 * For a caller that forks the call rather than awaiting it, so a change overtaken by the next can be interrupted — see `ConfiguratorMachine`. Interrupted, the request is aborted and nothing comes back.
 */
export const createConfigurationFromEffect = (position: ModelPosition, configuration: Configuration, items: ReadonlyArray<ISentConfigurationParameter>): Effect.Effect<Configuration, CreateConfigurationFromError, ApiServices> =>
	callDependencies.pipe(Effect.flatMap(({ client, modelPath }) => makeConfigurationCalls(client, modelPath).createConfigurationFrom(position, configuration, items)));
